import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme
} from '@mui/material';
import {
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Calculate as CalculateIcon,
  Settings as SettingsIcon,
  TrendingUp as TrendingUpIcon,
  YouTube as YouTubeIcon,
  Instagram as InstagramIcon,
  X as XIcon,
  Language as WebIcon,
  LinkedIn as LinkedInIcon,
  Forum as ThreadsIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import PlatformDialog from '../../../molecules/PlatformDialog/PlatformDialog';
import PlatformDialogTitle from '../../../molecules/PlatformDialog/PlatformDialogTitle';
import api from '../../../../services/api';
import { operationalBrandColors } from '../../../../design-system/tokens';

// Ícone customizado para TikTok (não existe no MUI)
const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor" {...props}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

// Ícone customizado para Facebook
const FacebookIcon = (props) => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor" {...props}>
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

// Ícone customizado para Pinterest
const PinterestIcon = (props) => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor" {...props}>
    <path d="M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12 0-6.628-5.373-12-12-12z" />
  </svg>
);

// Ícone customizado para Kwai (Geometria manual aproximada)
const KwaiIcon = (props) => (
  <svg viewBox="0 0 50 50" width="1em" height="1em" fill="currentColor" {...props}>
    {/* Corpo da câmera */}
    <rect x="8" y="15" width="34" height="25" rx="6" fill="currentColor" />
    {/* Carretel Esquerdo */}
    <circle cx="15" cy="12" r="5" fill="currentColor" />
    {/* Carretel Direito */}
    <circle cx="35" cy="12" r="5" fill="currentColor" />
    {/* Lente/Detalhe central (transparente ou vazado) */}
    <rect x="18" y="25" width="14" height="5" rx="2" fill="rgba(0,0,0,0.3)" />
  </svg>
);

const CPCManagement = () => {
  const theme = useTheme();
  const [cpcs, setCpcs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [recalculateDialogOpen, setRecalculateDialogOpen] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState(null);

  // Form states
  const [formData, setFormData] = useState({
    cpc: '',
    cpv: '',
    cpe: '',
    metric: 'likes'
  });

  const metrics = [
    { value: 'likes', label: 'Likes' },
    { value: 'views', label: 'Visualizações' },
    { value: 'engagement', label: 'Engajamento' },
    { value: 'impressions', label: 'Impressões' }
  ];

  // Função para gerar descrição automática baseada na plataforma e métrica
  const generateDescription = (platform, metric) => {
    const descriptions = {
      likes: 'Custo por like',
      views: 'Custo por visualização',
      engagement: 'Custo por engajamento',
      impressions: 'Custo por impressão'
    };

    return `${descriptions[metric] || 'Custo por'} no ${platform}`;
  };

  const fetchCPCs = async () => {
    try {
      setLoading(true);
      const response = await api.get('/cpc');
      setCpcs(response.data.data);
      setError('');
    } catch (error) {
      console.error('Erro ao buscar CPCs:', error);
      setError('Erro ao carregar configurações CPC: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCPCs();
  }, []);

  const handleEditClick = (platform) => {
    setSelectedPlatform(platform);
    setFormData({
      cpc: platform.cpc?.toString() || '0',
      cpv: platform.cpv?.toString() || '0.10',
      cpe: platform.cpe?.toString() || '0.50',
      metric: platform.metric,
      description: platform.description
    });
    setEditDialogOpen(true);
  };

  // Atualizar descrição quando a métrica muda
  const handleMetricChange = (metric) => {
    const newDescription = generateDescription(selectedPlatform?.platform, metric);
    setFormData(prev => ({
      ...prev,
      metric,
      description: newDescription
    }));
  };

  const handleRecalculateClick = (platform) => {
    setSelectedPlatform(platform);
    setRecalculateDialogOpen(true);
  };

  const handleUpdateCPC = async () => {
    try {
      await api.put(`/cpc/${selectedPlatform.platform}`, {
        cpc: parseFloat(formData.cpc) || 0,
        cpv: parseFloat(formData.cpv) || 0.10,
        cpe: parseFloat(formData.cpe) || 0.50,
        metric: formData.metric,
        description: formData.description
      });

      setSuccess(`Valoração Premium do ${selectedPlatform.platform} atualizada com sucesso!`);
      setEditDialogOpen(false);
      fetchCPCs();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Erro ao atualizar:', error);
      setError('Erro ao atualizar: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleRecalculate = async () => {
    try {
      setRecalculateDialogOpen(false);
      setLoading(true);

      const response = await api.post(`/cpc/recalculate/${selectedPlatform.platform}`);

      setSuccess(`Recálculo concluído: ${response.data.data.updated} artigos atualizados`);
      setTimeout(() => setSuccess(''), 5000);
    } catch (error) {
      console.error('Erro no recálculo:', error);
      setError('Erro no recálculo: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleInitializeDefaults = async () => {
    try {
      setLoading(true);
      await api.post('/cpc/init');
      setSuccess('Configurações padrão inicializadas com sucesso!');
      fetchCPCs();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Erro ao inicializar padrões:', error);
      setError('Erro ao inicializar padrões: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const getPlatformIcon = (platform) => {
    const iconProps = { sx: { fontSize: '1.5rem' } };
    const icons = {
      Instagram: <InstagramIcon {...iconProps} sx={{ ...iconProps.sx, color: '#E4405F' }} />,
      YouTube: <YouTubeIcon {...iconProps} sx={{ ...iconProps.sx, color: operationalBrandColors.youtube }} />,
      Twitter: <XIcon {...iconProps} />,
      TikTok: <TikTokIcon style={{ fontSize: '1.5rem' }} />,
      Facebook: <FacebookIcon style={{ fontSize: '1.5rem', color: '#1877F2' }} />,
      LinkedIn: <LinkedInIcon {...iconProps} sx={{ ...iconProps.sx, color: '#0A66C2' }} />,
      Pinterest: <PinterestIcon style={{ fontSize: '1.5rem', color: '#E60023' }} />,
      Threads: <ThreadsIcon {...iconProps} sx={{ ...iconProps.sx, color: '#ffffff' }} />,
      Kwai: <KwaiIcon style={{ fontSize: '1.5rem', color: '#ffffff' }} />,
      Online: <WebIcon {...iconProps} sx={{ ...iconProps.sx, color: '#4CAF50' }} />
    };
    return icons[platform] || <WebIcon {...iconProps} />;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  if (loading && cpcs.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <TrendingUpIcon sx={{ fontSize: 32, color: 'primary.main', mr: 1.5 }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Valoração Premium
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Configure CPV e CPE por tipo de mídia social
            </Typography>
          </Box>
        </Box>
        <Button
          variant="outlined"
          startIcon={<SettingsIcon />}
          onClick={handleInitializeDefaults}
          disabled={loading}
        >
          Inicializar Padrões
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Cards de resumo */}
        <Grid item xs={12}>
          <Grid container spacing={2}>
            {cpcs.map((cpc) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={cpc.platform}>
                <Card
                  sx={{
                    backgroundColor: theme.palette.background.paper,
                    border: 1, borderColor: 'divider',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      boxShadow: theme.shadows[4],
                      transform: 'translateY(-2px)'
                    }
                  }}
                >
                  <CardContent>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                      <Box sx={{ display: 'flex', alignItems: 'center', fontSize: '2rem' }}>
                        {getPlatformIcon(cpc.platform)}
                      </Box>
                      <Chip
                        label={cpc.metric}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </Box>

                    <Typography variant="h6" color="text.primary" gutterBottom>
                      {cpc.platform}
                    </Typography>

                    {/* CPV + CPE em destaque */}
                    <Box display="flex" gap={2} mb={1}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">CPV</Typography>
                        <Typography variant="h5" color="primary.main" sx={{ lineHeight: 1 }}>
                          {formatCurrency(cpc.cpv ?? 0.10)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">CPE</Typography>
                        <Typography variant="h5" color="secondary.main" sx={{ lineHeight: 1 }}>
                          {formatCurrency(cpc.cpe ?? 0.50)}
                        </Typography>
                      </Box>
                    </Box>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {cpc.description}
                    </Typography>

                    <Box display="flex" gap={1}>
                      <Tooltip title="Editar taxas">
                        <IconButton
                          size="small"
                          onClick={() => handleEditClick(cpc)}
                          color="primary"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Recalcular valorações">
                        <IconButton
                          size="small"
                          onClick={() => handleRecalculateClick(cpc)}
                          color="secondary"
                        >
                          <CalculateIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>

        {/* Tabela detalhada */}
        <Grid item xs={12}>
          <Card
            sx={{
              backgroundColor: theme.palette.background.paper,
              border: 1, borderColor: 'divider'
            }}
          >
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6" color="text.primary">
                  Configurações Detalhadas
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={fetchCPCs}
                  disabled={loading}
                >
                  Atualizar
                </Button>
              </Box>

              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Plataforma</TableCell>
                      <TableCell align="right">CPV</TableCell>
                      <TableCell align="right">CPE</TableCell>
                      <TableCell>Métrica</TableCell>
                      <TableCell>Descrição</TableCell>
                      <TableCell align="right">Última Atualização</TableCell>
                      <TableCell align="center">Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cpcs.map((cpc) => (
                      <TableRow key={cpc.platform} hover>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              {getPlatformIcon(cpc.platform)}
                            </Box>
                            <Typography variant="body1" fontWeight="medium">
                              {cpc.platform}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body1" fontWeight="medium" color="primary">
                            {formatCurrency(cpc.cpv ?? 0.10)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body1" fontWeight="medium" color="secondary">
                            {formatCurrency(cpc.cpe ?? 0.50)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={cpc.metric}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{cpc.description}</TableCell>
                        <TableCell align="right">
                          {new Date(cpc.lastUpdated).toLocaleDateString('pt-BR')}
                        </TableCell>
                        <TableCell align="center">
                          <Box display="flex" gap={1} justifyContent="center">
                            <Tooltip title="Editar">
                              <IconButton
                                size="small"
                                onClick={() => handleEditClick(cpc)}
                                color="primary"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Recalcular">
                              <IconButton
                                size="small"
                                onClick={() => handleRecalculateClick(cpc)}
                                color="secondary"
                              >
                                <CalculateIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Dialog de edição */}
      <PlatformDialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <PlatformDialogTitle 
          title={`Valoração Premium — ${selectedPlatform?.platform}`}
          subtitle="Configure as taxas de CPV e CPE para esta mídia."
          icon={selectedPlatform ? getPlatformIcon(selectedPlatform.platform) : <SettingsIcon />}
          onClose={() => setEditDialogOpen(false)}
        />
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
              Fórmula: <strong>Valoração = (Views × CPV) + ((Likes + Comentários + Shares) × CPE)</strong>
            </Alert>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="CPV (Custo por View)"
                  size="small"
                  type="number"
                  value={formData.cpv}
                  onChange={(e) => setFormData({ ...formData, cpv: e.target.value })}
                  inputProps={{ step: 0.01, min: 0 }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="CPE (Custo por Engajamento)"
                  size="small"
                  type="number"
                  value={formData.cpe}
                  onChange={(e) => setFormData({ ...formData, cpe: e.target.value })}
                  inputProps={{ step: 0.01, min: 0 }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>

            <TextField
              fullWidth
              select
              label="Métrica de Referência"
              size="small"
              value={formData.metric}
              onChange={(e) => handleMetricChange(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            >
              {metrics.map((metric) => (
                <MenuItem key={metric.value} value={metric.value}>
                  {metric.label}
                </MenuItem>
              ))}
            </TextField>
            
            <TextField
              fullWidth
              label="Descrição Curta"
              size="small"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setEditDialogOpen(false)} sx={{ fontWeight: 600 }}>Cancelar</Button>
          <Button
            onClick={handleUpdateCPC}
            variant="contained"
            disabled={!formData.cpv || !formData.cpe}
            sx={{ fontWeight: 700, borderRadius: 2, px: 4 }}
          >
            Salvar Taxas
          </Button>
        </DialogActions>
      </PlatformDialog>

      {/* Dialog de confirmação de recálculo */}
      <PlatformDialog open={recalculateDialogOpen} onClose={() => setRecalculateDialogOpen(false)} maxWidth="xs">
        <PlatformDialogTitle 
          title="Confirmar Recálculo"
          subtitle={`Processando: ${selectedPlatform?.platform}`}
          icon={<CalculateIcon />}
          onClose={() => setRecalculateDialogOpen(false)}
        />
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body1" fontWeight={500}>
            Deseja recalcular a valoração de todos os artigos de <Box component="span" sx={{ color: 'primary.main', fontWeight: 700 }}>{selectedPlatform?.platform}</Box>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            Esta operação processará toda a base histórica desta mídia e pode levar alguns minutos.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setRecalculateDialogOpen(false)} sx={{ fontWeight: 600 }}>Cancelar</Button>
          <Button 
            onClick={handleRecalculate} 
            variant="contained" 
            color="secondary"
            sx={{ fontWeight: 700, borderRadius: 2 }}
          >
            Iniciar Recálculo
          </Button>
        </DialogActions>
      </PlatformDialog>
    </Box>
  );
};

export default CPCManagement;
